import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { CalculateOutlined, CheckCircleOutlined, WarningAmberOutlined } from '@mui/icons-material';
import moment from 'moment';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../../shared/services/survey/survey-admin.service';
import { ISurveyCommittedEmissionsSummary, ISurveyExtrapolation } from '../../../../shared/services/survey/survey.model';
import { SurveyEmissionRows } from '../../../presentation-components/display-elements-components/survey-emission-rows';
import { SurveyEmissionsBreakdownDialog } from '../../../dialogs/surveys/survey-emissions-breakdown-dialog';
import { SurveyCommitEmissionsDialog } from '../../../dialogs/surveys/survey-commit-emissions-dialog';
import { SurveyExtrapolationDialog } from '../../../dialogs/surveys/survey-extrapolation-dialog';
import { SurveyRemoveCommittedEmissionsDialog } from '../../../dialogs/surveys/survey-remove-committed-emissions-dialog';
import { SurveyProductDataReviewDialog } from '../../../dialogs/surveys/survey-product-data-review-dialog';
import { SurveyHubTabProps } from '../survey-hub-page';

const fmtKg = (v: number | null | undefined) =>
  v == null || !Number.isFinite(v) ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtUnits = (v: number, unit?: string | null) =>
  `${v.toLocaleString(undefined, { maximumFractionDigits: 4 })}${unit ? ` ${unit}` : ''}`;

export const SurveyResultsTab = ({ survey }: SurveyHubTabProps) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const surveyId = survey.id;
  const isProductData = survey.purpose === 'SUPPLIER_PRODUCT_DATA';

  const [loading, setLoading] = useState(true);
  const [committedSummary, setCommittedSummary] = useState<ISurveyCommittedEmissionsSummary | null>(null);
  const [extrapolation, setExtrapolation] = useState<ISurveyExtrapolation | null>(null);
  const [commitOpen, setCommitOpen] = useState(false);
  const [aggregateOpen, setAggregateOpen] = useState(false);
  const [extrapolationOpen, setExtrapolationOpen] = useState(false);
  const [removeEmissionsOpen, setRemoveEmissionsOpen] = useState(false);
  const [productReviewOpen, setProductReviewOpen] = useState(false);

  const loadResults = useCallback(async () => {
    if (isProductData) { setLoading(false); return; }
    setLoading(true);
    const [summaryRes, extrapolationRes] = await Promise.allSettled([
      surveyAdminService.getSurveyCommittedEmissionsSummary(surveyId),
      surveyAdminService.getExtrapolation(surveyId),
    ]);
    setCommittedSummary(summaryRes.status === 'fulfilled' ? summaryRes.value : null);
    setExtrapolation(extrapolationRes.status === 'fulfilled' ? extrapolationRes.value : null);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId, isProductData]);

  useEffect(() => { loadResults(); }, [loadResults]);

  const notify = (message: string, isError?: boolean) => {
    userNotificationsContext.openSnackbar({
      open: true,
      message,
      backgroundColor: isError ? 'text.primary' : 'primary.dark',
    });
  };

  const handleReverseExtrapolation = () => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Remove extrapolation?',
      message: 'This will delete all extrapolated emission records. Committed responses will remain and you can commit more or re-extrapolate afterwards.',
      buttonCopy: 'Remove extrapolation',
      cancelButtonCopy: 'Cancel',
    }).then(async ok => {
      if (!ok) return;
      try {
        await surveyAdminService.reverseExtrapolation(surveyId);
        notify('Extrapolation removed');
        loadResults();
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to remove extrapolation';
        notify(msg, true);
      }
    });
  };

  if (isProductData) {
    return (
      <Box>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2, maxWidth: 720 }}>
          Suppliers&apos; submitted figures are held for review here. Applying them updates the
          matching products&apos; emission factors; nothing changes until you apply.
        </Typography>
        <Button
          variant='contained'
          startIcon={<CheckCircleOutlined />}
          onClick={() => setProductReviewOpen(true)}
        >
          Review and apply product data
        </Button>
        <SurveyProductDataReviewDialog
          open={productReviewOpen}
          onClose={() => setProductReviewOpen(false)}
          surveyId={surveyId}
          successHandler={() => { setProductReviewOpen(false); }}
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={18} />
        <Typography variant='body2' color='text.secondary'>Loading…</Typography>
      </Box>
    );
  }

  const committedTotal = (committedSummary?.rows ?? []).reduce((s, r) => s + (r.kgCo2e ?? 0), 0);
  const scaleFactor = extrapolation?.scaleFactor ?? 1;
  const increment = scaleFactor - 1;
  const kgByRuleId = new Map((committedSummary?.rows ?? []).map((r) => [r.ruleId, r.kgCo2e ?? 0]));
  const extrapolationTotal = extrapolation
    ? (extrapolation.surveyExtrapolationEmissions ?? []).reduce((s, line) => s + (kgByRuleId.get(line.ruleId ?? '') ?? 0) * increment, 0)
    : 0;
  const committedCount = committedSummary?.committedSessionCount ?? 0;

  return (
    <Box>
      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ mb: 3 }}>
        <Tooltip title={extrapolation ? 'Remove the active extrapolation before committing more responses' : ''}>
          <span>
            <Button
              variant='contained'
              startIcon={<CheckCircleOutlined />}
              onClick={() => setCommitOpen(true)}
              disabled={!!extrapolation}
            >
              Commit emissions to report period
            </Button>
          </span>
        </Tooltip>
        <Button
          variant='outlined'
          startIcon={<CalculateOutlined />}
          onClick={() => setAggregateOpen(true)}
        >
          Combined provisional emissions
        </Button>
        {extrapolation ? (
          <Button variant='outlined' color='warning' onClick={handleReverseExtrapolation}>
            Remove extrapolation
          </Button>
        ) : (
          <Tooltip title={committedCount === 0 ? 'Commit responses first: extrapolation scales committed results up to a target response count' : ''}>
            <span>
              <Button variant='outlined' disabled={committedCount === 0} onClick={() => setExtrapolationOpen(true)}>
                Extrapolate results
              </Button>
            </span>
          </Tooltip>
        )}
        {committedCount > 0 && (
          <Button variant='outlined' color='warning' onClick={() => setRemoveEmissionsOpen(true)}>
            Remove committed emissions
          </Button>
        )}
      </Stack>

      {extrapolation && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
          <Stack direction='row' spacing={1.5} alignItems='flex-start'>
            <WarningAmberOutlined sx={{ color: 'warning.main', mt: 0.25, flexShrink: 0 }} fontSize='small' />
            <Box>
              <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 0.5 }}>Extrapolation active</Typography>
              <Typography variant='body2' color='text.secondary'>
                Results extrapolated from <strong>{extrapolation.actualResponseCount}</strong> to{' '}
                <strong>{extrapolation.targetResponseCount}</strong> responses (×{extrapolation.scaleFactor.toFixed(2)} scale factor,{' '}
                {extrapolation.surveyExtrapolationEmissions?.length ?? 0} rule{(extrapolation.surveyExtrapolationEmissions?.length ?? 0) !== 1 ? 's' : ''} extrapolated).
                Committing more responses is blocked until this extrapolation is removed.
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}

      <Typography variant='h6' sx={{ mb: 2 }}>Committed results</Typography>

      {committedCount === 0 && (
        <Typography variant='body2' color='text.secondary'>
          No data has been committed from this survey yet. Committing turns submitted responses
          into emission records in the report period.
        </Typography>
      )}

      {committedSummary && committedCount > 0 && (
        <Stack spacing={1.5}>
          <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, alignSelf: 'flex-start' }}>
            <Typography variant='caption' color='text.secondary'>Committed responses:</Typography>
            <Typography variant='body2' fontWeight={700}>{committedSummary.committedSessionCount}</Typography>
          </Box>

          <SurveyEmissionRows
            unitsLabel='Units consumed'
            kgLabel='kg CO₂e'
            rows={committedSummary.rows.map((row) => ({
              key: row.ruleId,
              name: row.ruleName,
              units: fmtUnits(row.unitsConsumed, row.activityUnitName),
              kg: fmtKg(row.kgCo2e),
            }))}
            totalLabel='Total committed'
            totalKg={`${fmtKg(committedTotal)} kg CO₂e`}
          />

          {extrapolation && (
            <Box sx={{ mt: 0.5, p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                <WarningAmberOutlined sx={{ color: 'warning.main' }} fontSize='small' />
                <Typography variant='subtitle2' fontWeight={600}>Extrapolation active</Typography>
              </Stack>
              <Stack direction='row' spacing={3} sx={{ mb: 1.5 }} flexWrap='wrap' useFlexGap>
                {[
                  { label: 'Actual responses', value: String(extrapolation.actualResponseCount) },
                  { label: 'Target responses', value: String(extrapolation.targetResponseCount) },
                  { label: 'Scale factor', value: `×${extrapolation.scaleFactor.toFixed(3)}` },
                  { label: 'Created', value: moment(extrapolation.createdAt).format('DD/MM/YYYY') },
                ].map(({ label, value }) => (
                  <Box key={label}>
                    <Typography variant='caption' color='text.secondary'>{label}</Typography>
                    <Typography variant='body2' fontWeight={700}>{value}</Typography>
                  </Box>
                ))}
              </Stack>

              {(extrapolation.surveyExtrapolationEmissions ?? []).length > 0 && (
                <SurveyEmissionRows
                  unitsLabel='Units added'
                  kgLabel='kg CO₂e added'
                  rows={(extrapolation.surveyExtrapolationEmissions ?? []).map((line) => ({
                    key: line.id,
                    name: line.ruleName,
                    units: `+${line.extrapolatedUnitsConsumed.toLocaleString(undefined, { maximumFractionDigits: 4 })}`,
                    kg: `+${fmtKg((kgByRuleId.get(line.ruleId ?? '') ?? 0) * increment)}`,
                  }))}
                  totalLabel='Total with extrapolation'
                  totalKg={`${fmtKg(committedTotal + extrapolationTotal)} kg CO₂e`}
                />
              )}
            </Box>
          )}
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant='caption' color='text.secondary'>
        Provisional figures are estimates from saved answers; only committed figures appear in reports.
      </Typography>

      <SurveyCommitEmissionsDialog
        open={commitOpen}
        surveyId={surveyId}
        onClose={() => setCommitOpen(false)}
        onCommitted={loadResults}
        onNotify={notify}
      />
      <SurveyEmissionsBreakdownDialog
        mode='aggregate'
        open={aggregateOpen}
        surveyId={surveyId}
        onClose={() => setAggregateOpen(false)}
      />
      <SurveyExtrapolationDialog
        open={extrapolationOpen}
        surveyId={surveyId}
        committedResponseCount={committedCount}
        onClose={() => setExtrapolationOpen(false)}
        onCreated={() => { setExtrapolationOpen(false); loadResults(); }}
      />
      <SurveyRemoveCommittedEmissionsDialog
        open={removeEmissionsOpen}
        surveyId={surveyId}
        hasActiveExtrapolation={!!extrapolation}
        onClose={() => setRemoveEmissionsOpen(false)}
        onRemoved={loadResults}
        onNotify={notify}
      />
    </Box>
  );
};
